import React, { useState, useEffect, useCallback } from 'react';

import { useAdminApi } from "../../adminUtils";

function fmt$(n) {
  return '$' + (n || 0).toFixed(2);
}

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return new Date(+y, +m - 1, +d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function PayrollReport() {
  const { API, ADMIN_SECRET } = useAdminApi();
  const [from, setFrom] = useState(addDays(today(), -27));
  const [to, setTo]     = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API}/reports/payroll?from=${from}&to=${to}`,
        { headers: { Authorization: `Bearer ${ADMIN_SECRET}` } }
      );
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, API, ADMIN_SECRET]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const shiftRange = (weeks) => {
    const days = weeks * 7;
    setFrom(addDays(from, days));
    setTo(addDays(to, days));
  };

  const exportXlsx = async () => {
    if (!data) return;
    const XLSX = await import('xlsx');

    const wb = XLSX.utils.book_new();

    // ── Classes sheet ──────────────────────────────────────────────────────
    const classRows = [];
    for (const [teacher, rows] of Object.entries(data.classes || {})) {
      for (const r of rows) {
        classRows.push({
          Teacher: teacher,
          Date: r.date,
          Class: r.title,
          Students: r.attended,
          'Rate/Student': r.ratePerStudent,
          'Pay Ceiling': r.ceiling || '—',
          'Gross Revenue': r.grossRevenue.toFixed(2),
          'Teacher Pay': r.teacherPay.toFixed(2),
          'Studio Take': r.studioTake.toFixed(2),
        });
      }
      // Subtotal row per teacher
      const sub = rows.reduce(
        (acc, r) => ({
          ...acc,
          students: acc.students + r.attended,
          gross: acc.gross + r.grossRevenue,
          pay: acc.pay + r.teacherPay,
          studio: acc.studio + r.studioTake,
        }),
        { students: 0, gross: 0, pay: 0, studio: 0 }
      );
      classRows.push({
        Teacher: `TOTAL — ${teacher}`,
        Date: '',
        Class: '',
        Students: sub.students,
        'Rate/Student': '',
        'Pay Ceiling': '',
        'Gross Revenue': sub.gross.toFixed(2),
        'Teacher Pay': sub.pay.toFixed(2),
        'Studio Take': sub.studio.toFixed(2),
      });
    }
    const wsClasses = XLSX.utils.json_to_sheet(classRows);
    wsClasses['!cols'] = [
      { wch: 18 }, { wch: 14 }, { wch: 28 }, { wch: 10 },
      { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, wsClasses, 'Classes');

    // ── Workshops sheet ────────────────────────────────────────────────────
    const wsRows = (data.workshops || []).map(r => ({
      Date: r.date,
      Workshop: r.title,
      Teacher: r.teacher,
      Participants: r.participants,
      'Workshop Price': r.workshopPrice.toFixed(2),
      'Teacher Split %': r.teacherSplit,
      'Gross Revenue': r.grossRevenue.toFixed(2),
      'Teacher Pay': r.teacherPay.toFixed(2),
      'Studio Take': r.studioTake.toFixed(2),
    }));
    const wsWorkshops = XLSX.utils.json_to_sheet(wsRows.length ? wsRows : [{ Note: 'No workshops in range' }]);
    wsWorkshops['!cols'] = [
      { wch: 14 }, { wch: 28 }, { wch: 18 }, { wch: 12 },
      { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, wsWorkshops, 'Workshops');

    // ── Summary sheet ──────────────────────────────────────────────────────
    const summaryRows = [
      { Category: 'Classes — Gross Revenue', Amount: data.classTotals.grossRevenue.toFixed(2) },
      { Category: 'Classes — Teacher Pay', Amount: data.classTotals.teacherPay.toFixed(2) },
      { Category: 'Classes — Studio Take', Amount: data.classTotals.studioTake.toFixed(2) },
      { Category: '' },
      { Category: 'Workshops — Gross Revenue', Amount: data.workshopTotals.grossRevenue.toFixed(2) },
      { Category: 'Workshops — Teacher Pay', Amount: data.workshopTotals.teacherPay.toFixed(2) },
      { Category: 'Workshops — Studio Take', Amount: data.workshopTotals.studioTake.toFixed(2) },
      { Category: '' },
      { Category: 'GRAND TOTAL — Gross Revenue', Amount: data.grandTotal.grossRevenue.toFixed(2) },
      { Category: 'GRAND TOTAL — Teacher Pay', Amount: data.grandTotal.teacherPay.toFixed(2) },
      { Category: 'GRAND TOTAL — Studio Take', Amount: data.grandTotal.studioTake.toFixed(2) },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 36 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    XLSX.writeFile(wb, `payroll-${from}-to-${to}.xlsx`);
  };

  const teachers = data ? Object.keys(data.classes || {}).sort() : [];

  return (
    <div className="adm-report-section">
      <div className="adm-report-section-title">R4 — Payroll</div>

      {/* Date range controls */}
      <div className="adm-report-range" style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button className="adm-btn-export" onClick={() => shiftRange(-1)}>← Prev 4w</button>
        <span className="adm-range-label">
          {fmtDate(from)} – {fmtDate(to)}
        </span>
        <button className="adm-btn-export" onClick={() => shiftRange(1)}>Next 4w →</button>
        <input
          type="date"
          className="adm-input"
          style={{ width: '140px' }}
          value={from}
          onChange={e => setFrom(e.target.value)}
        />
        <span style={{ color: 'var(--color-grey-400)', fontSize: '0.85rem' }}>to</span>
        <input
          type="date"
          className="adm-input"
          style={{ width: '140px' }}
          value={to}
          onChange={e => setTo(e.target.value)}
        />
        <button className="adm-btn--primary" onClick={fetchPayroll} disabled={loading}>
          {loading ? 'Loading…' : 'Run'}
        </button>
        {data && (
          <button className="adm-btn-export" onClick={exportXlsx}>
            Export XLSX
          </button>
        )}
      </div>

      {error && (
        <div className="adm-empty" style={{ color: 'var(--color-error, #c0392b)', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {loading && <div className="adm-empty">Loading payroll…</div>}

      {data && !loading && (
        <>
          {/* ── All summary chips on one line ────────────────────────────── */}
          {(() => {
            const totalClasses  = teachers.reduce((acc, t) => acc + data.classes[t].length, 0);
            const totalStudents = teachers.reduce((acc, t) => acc + data.classes[t].reduce((a, r) => a + r.attended, 0), 0);
            return (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                <div className="adm-summary-chip">
                  <span className="adm-summary-label">Gross Revenue</span>
                  <strong>{fmt$(data.grandTotal.grossRevenue)}</strong>
                </div>
                <div className="adm-summary-chip adm-summary-chip--plum">
                  <span className="adm-summary-label">Teacher Pay</span>
                  <strong>{fmt$(data.grandTotal.teacherPay)}</strong>
                </div>
                <div className="adm-summary-chip adm-summary-chip--green">
                  <span className="adm-summary-label">Studio Take</span>
                  <strong>{fmt$(data.grandTotal.studioTake)}</strong>
                </div>
                <div className="adm-summary-chip">
                  <span className="adm-summary-label">Classes</span>
                  <strong>{totalClasses}</strong>
                </div>
                <div className="adm-summary-chip">
                  <span className="adm-summary-label">Students</span>
                  <strong>{totalStudents}</strong>
                </div>
                <div className="adm-summary-chip">
                  <span className="adm-summary-label">Teachers</span>
                  <strong>{teachers.length}</strong>
                </div>
              </div>
            );
          })()}

          {/* ── Per-teacher summary table ────────────────────────────────── */}
          {teachers.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <table className="adm-report-table" style={{ width: '100%', maxWidth: '520px' }}>
                <thead>
                  <tr>
                    <th>Teacher</th>
                    <th style={{ textAlign: 'right' }}>Classes</th>
                    <th style={{ textAlign: 'right' }}>Students</th>
                    <th style={{ textAlign: 'right' }}>Pay</th>
                    <th style={{ textAlign: 'right' }}>Studio</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map(t => {
                    const rows = data.classes[t];
                    const sub  = rows.reduce((acc, r) => ({
                      classes:  acc.classes  + 1,
                      students: acc.students + r.attended,
                      pay:      acc.pay      + r.teacherPay,
                      studio:   acc.studio   + r.studioTake,
                    }), { classes: 0, students: 0, pay: 0, studio: 0 });
                    return (
                      <tr key={t}>
                        <td>{t}</td>
                        <td style={{ textAlign: 'right' }}>{sub.classes}</td>
                        <td style={{ textAlign: 'right' }}>{sub.students}</td>
                        <td style={{ textAlign: 'right', color: sub.pay > 0 ? 'var(--color-tertiary)' : undefined }}>{fmt$(sub.pay)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt$(sub.studio)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Classes section ─────────────────────────────────────────── */}
          <div className="adm-report-section-title" style={{ marginBottom: '0.75rem' }}>
            Classes
          </div>

          {teachers.length === 0 ? (
            <div className="adm-empty">No class bookings in this range.</div>
          ) : (
            teachers.map(teacher => {
              const rows = data.classes[teacher];
              const sub = rows.reduce(
                (acc, r) => ({
                  attended: acc.attended + r.attended,
                  gross: acc.gross + r.grossRevenue,
                  pay: acc.pay + r.teacherPay,
                  studio: acc.studio + r.studioTake,
                }),
                { attended: 0, gross: 0, pay: 0, studio: 0 }
              );

              return (
                <div key={teacher} className="adm-report-teacher" style={{ marginBottom: '1.5rem' }}>
                  <div className="adm-report-teacher-header">
                    <span className="adm-report-teacher-name">{teacher}</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="adm-report-table" style={{ width: '100%', minWidth: '600px' }}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Class</th>
                          <th style={{ textAlign: 'right' }}>Students</th>
                          <th style={{ textAlign: 'right' }}>Rate</th>
                          <th style={{ textAlign: 'right' }}>Ceiling</th>
                          <th style={{ textAlign: 'right' }}>Revenue</th>
                          <th style={{ textAlign: 'right' }}>Teacher</th>
                          <th style={{ textAlign: 'right' }}>Studio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i}>
                            <td>{fmtDate(r.date)}</td>
                            <td>{r.title}</td>
                            <td style={{ textAlign: 'right' }}>{r.attended}</td>
                            <td style={{ textAlign: 'right' }}>
                              {r.ratePerStudent > 0 ? fmt$(r.ratePerStudent) : <span className="adm-muted">—</span>}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {r.ceiling > 0 ? fmt$(r.ceiling) : <span className="adm-muted">none</span>}
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmt$(r.grossRevenue)}</td>
                            <td style={{ textAlign: 'right', color: r.teacherPay > 0 ? 'var(--color-tertiary)' : undefined }}>
                              {fmt$(r.teacherPay)}
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmt$(r.studioTake)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="adm-report-subtotal">
                          <td colSpan={2}><strong>Subtotal</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{sub.attended}</strong></td>
                          <td colSpan={2} />
                          <td style={{ textAlign: 'right' }}><strong>{fmt$(sub.gross)}</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{fmt$(sub.pay)}</strong></td>
                          <td style={{ textAlign: 'right' }}><strong>{fmt$(sub.studio)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              );
            })
          )}

          {/* ── Workshops section ───────────────────────────────────────── */}
          <div className="adm-report-section-title" style={{ margin: '1.5rem 0 0.75rem' }}>
            Workshops
          </div>

          {data.workshops.length === 0 ? (
            <div className="adm-empty">No workshop bookings in this range.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="adm-report-table" style={{ width: '100%', minWidth: '640px' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Workshop</th>
                      <th>Teacher</th>
                      <th style={{ textAlign: 'right' }}>Participants</th>
                      <th style={{ textAlign: 'right' }}>Split</th>
                      <th style={{ textAlign: 'right' }}>Revenue</th>
                      <th style={{ textAlign: 'right' }}>Teacher</th>
                      <th style={{ textAlign: 'right' }}>Studio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.workshops.map((r, i) => (
                      <tr key={i}>
                        <td>{fmtDate(r.date)}</td>
                        <td>{r.title}</td>
                        <td>{r.teacher}</td>
                        <td style={{ textAlign: 'right' }}>{r.participants}</td>
                        <td style={{ textAlign: 'right' }}>{r.teacherSplit}%</td>
                        <td style={{ textAlign: 'right' }}>{fmt$(r.grossRevenue)}</td>
                        <td style={{ textAlign: 'right', color: r.teacherPay > 0 ? 'var(--color-tertiary)' : undefined }}>
                          {fmt$(r.teacherPay)}
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt$(r.studioTake)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="adm-report-subtotal">
                      <td colSpan={3}><strong>Subtotal</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{data.workshopTotals.participants}</strong></td>
                      <td />
                      <td style={{ textAlign: 'right' }}><strong>{fmt$(data.workshopTotals.grossRevenue)}</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{fmt$(data.workshopTotals.teacherPay)}</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{fmt$(data.workshopTotals.studioTake)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {/* ── Note about unset rates ──────────────────────────────────── */}
          {teachers.some(t =>
            data.classes[t].some(r => r.ratePerStudent === 0)
          ) && (
            <p className="adm-muted" style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
              ⚠ Some classes have no rate/student set — teacher pay shows $0.00 for those rows.
              Set rates in the class editor (Calendar tab → edit class).
            </p>
          )}
        </>
      )}
    </div>
  );
}
